import { SENTRY_URL } from '@/constants'
import { APP_ENV, GIT_COMMIT_SHA, IS_PROD_BUILD } from '@/environment'
import * as Sentry from '@sentry/vue'
import Vue from 'vue'

/**
 * Whether an event is worth sending. Every rule names the Sentry issue it
 * suppresses and why the app already handles the outcome — a rule with no
 * issue behind it does not belong here.
 *
 * Kept a pure function over the Sentry event so it can be exercised directly.
 */
export const isReportableEvent = (event) => {
  const exceptions = event.exception?.values ?? []

  // ADMIN-21Q1, ADMIN-2ED — an expired access token. createAttensiFetch calls
  // redirectToLogin() before it throws, so the user is already on their way
  // back to sign-in by the time this would be reported.
  const isExpiredToken = exceptions.some(
    ({ type, value }) =>
      type === 'AttensiFetchError' && value === 'HTTP status code 401'
  )

  return !isExpiredToken
}

export const initializeSentry = (subdomain, company, router) => {
  if (!GIT_COMMIT_SHA) return

  Sentry.init({
    Vue,
    dsn: SENTRY_URL,
    beforeSend: (event) => (isReportableEvent(event) ? event : null),
    enabled: IS_PROD_BUILD,
    environment: APP_ENV,
    release: GIT_COMMIT_SHA,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [
          /^https:\/\/api\.attensi\.com\/api(?!\/v1\/chost\/)/,
        ],
        networkCaptureBodies: true,
      }),
    ],
    // This will make 100% of all transactions be sent to Sentry.
    tracesSampleRate: 1,
    // profile sample rate is relative to the trace sample rate. This in effect will sample profiles for 10% of the sampled traces.
    profilesSampleRate: 0.1, // js-profiling NEEDS TO BE INCLUDED IN THE RESPONSE HEADER FROM THE BACK-END FOR THIS TO WORK
    // Session replay for all sessions where an error occurs
    // At the moment we only use Sentry for all our logs from Engage, and therefore want to all transactions to be present in case we need to debug or look at specific cases.
    // In the future we can use Sentry's "tracesSampler" to be more selective about what is sampled.
    replaysOnErrorSampleRate: 1.0,
    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    // Exclude contenthost untill it supports trace propagation
    tracePropagationTargets: [
      /^https:\/\/api\.attensi\.com\/api(?!\/v1\/chost\/)/,
      /^https:\/\/auth\.attensi\.com\/api/,
    ],
  })

  const impersonation = window.location.href.includes('impersonation=true')

  Sentry.setContext('Company', {
    subdomain,
    company,
    impersonation,
  })
}
